import React, { useRef, useState } from 'react';
import {
  MotionValue,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from 'motion/react';
import { Box, Container, Stack, Typography, alpha } from '@mui/material';
import { useMotionSafe } from '../../../motion';
import { fontFamilyDisplay, glory, grey } from '../../../theme/tokens';
import GarageHeading from './GarageHeading';
import V8Drawing, { TEARDOWN } from './V8Drawing';
import { gaugeNumber, hazardStripe, steel, stencil } from './garage';

/**
 * "Under the hood": a small-block V8 that comes apart as you scroll.
 *
 * The section is several screens tall and its inner frame sticks below the
 * app bar, so the page scrolls *through* it: the line art draws in while the
 * section arrives, then the scroll scrubs the teardown, and the parts legend
 * lights up part by part. Under reduced motion nothing pins or moves — the
 * section is one screen showing the finished exploded diagram.
 */

/** Which teardown step the scroll has reached; -1 before the first part comes off. */
const stepAt = (progress: number) => {
  let step = -1;
  TEARDOWN.forEach((part, index) => {
    if (progress >= part.range[0]) step = index;
  });
  return step;
};

const LegendRow: React.FC<{ index: number; teardown: MotionValue<number> }> = ({ index, teardown }) => {
  const start = TEARDOWN[index].range[0];
  const on = useTransform(teardown, [start, start + 0.05], [0, 1], { clamp: true });
  const badge = useTransform(on, [0, 1], [alpha(grey[0], 0), glory[600]]);
  const rim = useTransform(on, [0, 1], [alpha(grey[0], 0.28), glory[600]]);
  const color = useTransform(on, [0, 1], [grey[500], grey[50]]);
  const x = useTransform(on, [0, 1], [0, 6]);

  return (
    <Box
      component={motion.li}
      style={{ color, x }}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.75, py: 0.6 }}
    >
      <Box
        component={motion.span}
        aria-hidden
        style={{ backgroundColor: badge, borderColor: rim }}
        sx={{
          ...gaugeNumber,
          width: 26,
          height: 26,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          border: '1.5px solid',
          fontSize: '0.9375rem',
          color: grey[0],
        }}
      >
        {index + 1}
      </Box>
      <Typography component="span" sx={{ ...stencil, fontSize: '0.875rem', color: 'inherit' }}>
        {TEARDOWN[index].name}
      </Typography>
    </Box>
  );
};

const TeardownGauge: React.FC<{ teardown: MotionValue<number> }> = ({ teardown }) => {
  const percent = useTransform(teardown, (v) => `${Math.round(Math.min(1, v / TEARDOWN[TEARDOWN.length - 1].range[1]) * 100)}%`);
  const fill = useTransform(teardown, [0, TEARDOWN[TEARDOWN.length - 1].range[1]], [0, 1], { clamp: true });
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography component="span" sx={{ ...stencil, color: grey[400] }}>
          Teardown
        </Typography>
        <motion.span style={{ ...stencil, color: grey[50] }}>{percent}</motion.span>
      </Stack>
      <Box sx={{ height: 4, backgroundColor: alpha(grey[0], 0.12), overflow: 'hidden' }}>
        <Box
          component={motion.div}
          style={{ scaleX: fill, transformOrigin: 'left center' }}
          sx={{ height: '100%', backgroundColor: glory[600] }}
        />
      </Box>
    </Box>
  );
};

const EngineTeardown: React.FC = () => {
  const { enabled } = useMotionSafe();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress: arriving } = useScroll({ target: ref, offset: ['start end', 'start start'] });
  const { scrollYProgress: pinned } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const finished = useMotionValue(1);
  const entry = enabled ? arriving : finished;
  const teardown = enabled ? pinned : finished;

  // The phone readout re-renders only when the step changes, not every frame.
  const [step, setStep] = useState(() => stepAt(teardown.get()));
  useMotionValueEvent(teardown, 'change', (value) => {
    const next = stepAt(value);
    if (next !== step) setStep(next);
  });

  return (
    <Box
      ref={ref}
      component="section"
      sx={{ ...steel, position: 'relative', height: enabled ? { xs: '280vh', md: '320vh' } : 'auto' }}
    >
      <Box
        sx={{
          position: enabled ? 'sticky' : 'relative',
          top: { xs: 60, md: 68 },
          height: enabled ? { xs: 'calc(100svh - 60px)', md: 'calc(100svh - 68px)' } : 'auto',
          overflow: 'hidden',
        }}
      >
        <Container
          sx={{
            height: '100%',
            pt: { xs: 3, md: 4 },
            pb: enabled ? { xs: 3, md: 4 } : { xs: 6, md: 8 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' },
            gridTemplateRows: { xs: 'auto minmax(0, 1fr) auto', md: 'minmax(0, 1fr)' },
            columnGap: 6,
            alignItems: 'center',
          }}
        >
          <Box>
            <GarageHeading
              onSteel
              eyebrow="Under the hood"
              title={'American V8.\nInside and out.'}
              highlight={['Inside and out.']}
              description={
                // Under reduced motion the diagram is already apart; there is nothing to scroll for.
                `${enabled ? 'Scroll to tear it down. ' : ''}Every part we pull gets inspected, measured and put back to spec.`
              }
            />

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="ol"
                aria-label="Parts, in the order they come off"
                sx={{ listStyle: 'none', m: 0, p: 0, mb: 3.5 }}
              >
                {TEARDOWN.map((part, index) => (
                  <LegendRow key={part.name} index={index} teardown={teardown} />
                ))}
              </Box>
              <TeardownGauge teardown={teardown} />
            </Box>
          </Box>

          <Box
            sx={{
              height: enabled ? '100%' : { xs: 480, md: 820 },
              minHeight: 0,
              py: { xs: 1, md: 2 },
            }}
          >
            <V8Drawing entry={entry} teardown={teardown} />
          </Box>

          {/* Phone: the part coming off now, in place of the full legend */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, pt: 1 }} aria-live="polite">
            <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.25 }}>
              <Typography component="span" sx={{ ...gaugeNumber, fontSize: '1.75rem', lineHeight: 1, color: glory[400] }}>
                {step < 0 ? '—' : String(step + 1).padStart(2, '0')}
              </Typography>
              <Typography
                component="span"
                sx={{ fontFamily: fontFamilyDisplay, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: grey[50] }}
              >
                {step < 0 ? 'Scroll to tear it down' : TEARDOWN[step].name}
              </Typography>
            </Stack>
            <TeardownGauge teardown={teardown} />
          </Box>
        </Container>
      </Box>
      <Box aria-hidden sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, ...hazardStripe(8) }} />
    </Box>
  );
};

export default EngineTeardown;
